import BenchlingConfig from 'BenchlingConfig';
import staticURL from 'lib/staticURL';

// Constants for known models (for hardcoded references in code)
export const SCIENTIFIC_MODEL = {
  ALPHAFOLD: 'alphafold', // This is called AlphaFold in the code, but AlphaFold2 in the UI
  CHAI1: 'chai1',
  BOLTZ2: 'boltz2',
  OPENFOLD2: 'openfold2', // This is called OpenFold2 in the code, but OpenFold in the UI
  OPENFOLD3: 'openfold3',
  BOLTZGEN: 'boltzgen',
} as const;

// Frontend constants for scientific models
// Keep in sync with StructurePredictionModel in benchling/molbio/aa_sequences/models/structure_prediction_run.py

const MIN_AMINO_ACID_COUNT = 1;
const MAX_AMINO_ACID_COUNT = 1500;

// Keep in sync with MAX_PREDICTIONS_PER_MODEL_RUN in benchling/model_hub/connectors/model_run_connector.py
export const MAX_BULK_PREDICTION_ITEMS = 25;

// Maximum number of components allowed in a multimer prediction
// Keep in sync with MAX_MULTIMER_COMPONENTS in benchling/molbio/aa_sequences/structure_prediction/constants.py
const MAX_MULTIMER_COMPONENTS = 25;

// Keep in sync with MAX_CHAI1_TOKEN_COUNT in benchling/molbio/aa_sequences/structure_prediction/constants.py
const MAX_CHAI1_TOKEN_COUNT = 2048;

/**
 * Check if a model string is a known/valid model.
 */
export function isValidModel(model: string): boolean {
  return Object.hasOwn(SCIENTIFIC_MODEL_CONFIG, model);
}

const MODEL_CATEGORY = {
  STRUCTURE_PREDICTION: 'structure_prediction',
  GENERATIVE: 'generative',
};

export interface ScientificModelConfig {
  category: (typeof MODEL_CATEGORY)[keyof typeof MODEL_CATEGORY];
  // Display & UI
  displayName: string;
  description: string;
  iconUrl: string;
  externalLink: string;
  paperLink?: string; // Optional link to the research paper (falls back to externalLink if not provided)

  // Capabilities
  supportsMultimers: boolean;
  supportsMulitmerMolecules: boolean;
  egressesData: boolean;

  // Limits (null means not applicable)
  minAminoAcidCount: number | null;
  maxAminoAcidCount: number | null;
  maxTokenCount: number | null;
  maxMultimerComponents: number | null;

  // Feature flags & availability
  requiresFeatureFlags: Array<keyof typeof BenchlingConfig>; // e.g., 'MODEL_HUB_ENABLE_STRUCTURE_PREDICTION_MODELS'
  isPreview: boolean;
}

/**
 * Unified configuration for all scientific models.
 * This is the single source of truth for all model-specific data.
 */
export const SCIENTIFIC_MODEL_CONFIG: Record<string, ScientificModelConfig> = {
  [SCIENTIFIC_MODEL.ALPHAFOLD]: {
    category: MODEL_CATEGORY.STRUCTURE_PREDICTION,
    displayName: 'AlphaFold2',
    description:
      "AlphaFold2 is an AI system developed by DeepMind that predicts a protein's 3D structure from its amino acid sequence with remarkable accuracy. It uses a novel machine learning approach that incorporates physical and biological knowledge about protein structure.",
    iconUrl: staticURL('/static/img/model_hub/alphafold2.png'),
    externalLink: 'https://deepmind.google/science/alphafold/',
    paperLink: 'https://www.nature.com/articles/s41586-021-03819-2',
    supportsMultimers: false,
    supportsMulitmerMolecules: false,
    egressesData: false,
    minAminoAcidCount: MIN_AMINO_ACID_COUNT,
    maxAminoAcidCount: MAX_AMINO_ACID_COUNT,
    maxTokenCount: null,
    maxMultimerComponents: null,
    requiresFeatureFlags: ['MOLBIO_ENABLE_ALPHAFOLD_GA'],
    isPreview: false,
  },
  [SCIENTIFIC_MODEL.CHAI1]: {
    category: MODEL_CATEGORY.STRUCTURE_PREDICTION,
    displayName: 'Chai-1',
    description:
      'Chai-1 is a multi-modal foundation model for molecular structure prediction. It can predict the joint 3D structure of proteins, small molecules, DNA, RNA, and covalent modifications with state-of-the-art accuracy.',
    iconUrl: staticURL('/static/img/model_hub/chai1.png'),
    externalLink: 'https://www.chaidiscovery.com/news/introducing-chai-1',
    supportsMultimers: true,
    supportsMulitmerMolecules: true,
    egressesData: false,
    minAminoAcidCount: MIN_AMINO_ACID_COUNT,
    maxAminoAcidCount: MAX_AMINO_ACID_COUNT,
    maxTokenCount: MAX_CHAI1_TOKEN_COUNT,
    maxMultimerComponents: MAX_MULTIMER_COMPONENTS,
    requiresFeatureFlags: ['MOLBIO_ENABLE_ALPHAFOLD_GA', 'MODEL_HUB_ENABLE_STRUCTURE_PREDICTION_MODELS'],
    isPreview: false,
  },
  [SCIENTIFIC_MODEL.BOLTZ2]: {
    category: MODEL_CATEGORY.STRUCTURE_PREDICTION,
    displayName: 'Boltz-2',
    description:
      'Boltz-2 is a new biomolecular foundation model that goes beyond AlphaFold3 and Boltz-1 by jointly modeling complex structures and binding affinities, a critical component towards accurate molecular design. Boltz-2 is the first deep learning model to approach the accuracy of physics-based free-energy perturbation (FEP) methods, while running 1000x faster — making accurate in silico screening practical for early-stage drug discovery.',
    iconUrl: staticURL('/static/img/model_hub/boltz2.png'),
    externalLink: 'https://boltz.bio/boltz2',
    supportsMultimers: true,
    supportsMulitmerMolecules: true,
    egressesData: false,
    minAminoAcidCount: MIN_AMINO_ACID_COUNT,
    maxAminoAcidCount: MAX_AMINO_ACID_COUNT,
    maxTokenCount: MAX_CHAI1_TOKEN_COUNT,
    maxMultimerComponents: MAX_MULTIMER_COMPONENTS,
    requiresFeatureFlags: ['MOLBIO_ENABLE_ALPHAFOLD_GA', 'MODEL_HUB_ENABLE_STRUCTURE_PREDICTION_MODELS'],
    isPreview: false,
  },
  [SCIENTIFIC_MODEL.BOLTZGEN]: {
    category: MODEL_CATEGORY.GENERATIVE,
    displayName: 'BoltzGen',
    description:
      'BoltzGen is a generative model that designs novel protein sequences optimized to bind to a target molecule. It leverages the Boltz architecture to generate diverse, high-quality binder candidates for drug discovery applications.',
    iconUrl: staticURL('/static/img/model_hub/boltz2.png'),
    externalLink: 'https://boltz.bio/boltzgen',
    supportsMultimers: false,
    supportsMulitmerMolecules: false,
    egressesData: false,
    minAminoAcidCount: null,
    maxAminoAcidCount: null,
    maxTokenCount: null,
    maxMultimerComponents: null,
    requiresFeatureFlags: ['MODEL_HUB_ENABLE_GENERATIVE_MODELS_TAB'],
    isPreview: true,
  },
  [SCIENTIFIC_MODEL.OPENFOLD2]: {
    category: MODEL_CATEGORY.STRUCTURE_PREDICTION,
    displayName: 'OpenFold',
    description:
      'OpenFold is an open-source implementation of AlphaFold2 that provides accurate protein structure prediction. It offers the same high-quality predictions as AlphaFold2 with a focus on accessibility and reproducibility.',
    iconUrl: staticURL('/static/img/model_hub/openfold2.png'),
    externalLink: 'https://www.biorxiv.org/content/10.1101/2022.11.20.517210v3',
    supportsMultimers: false,
    supportsMulitmerMolecules: false,
    egressesData: false,
    minAminoAcidCount: 16,
    maxAminoAcidCount: MAX_AMINO_ACID_COUNT,
    maxTokenCount: null,
    maxMultimerComponents: MAX_MULTIMER_COMPONENTS,
    requiresFeatureFlags: [
      'MOLBIO_ENABLE_ALPHAFOLD_GA',
      'MODEL_HUB_ENABLE_STRUCTURE_PREDICTION_MODELS',
      'MODEL_HUB_ENABLE_OPENFOLD2',
    ],
    isPreview: true,
  },
  [SCIENTIFIC_MODEL.OPENFOLD3]: {
    category: MODEL_CATEGORY.STRUCTURE_PREDICTION,
    displayName: 'OpenFold3',
    description:
      "OpenFold3 is a biomolecular structure prediction model aiming to be a bitwise reproduction of DeepMind's AlphaFold3.",
    iconUrl: staticURL('/static/img/model_hub/openfold2.png'),
    externalLink: 'https://github.com/aqlaboratory/openfold-3',
    supportsMultimers: false,
    supportsMulitmerMolecules: false,
    egressesData: false,
    minAminoAcidCount: 16,
    maxAminoAcidCount: MAX_AMINO_ACID_COUNT,
    maxTokenCount: null,
    maxMultimerComponents: MAX_MULTIMER_COMPONENTS,
    requiresFeatureFlags: [
      'MOLBIO_ENABLE_ALPHAFOLD_GA',
      'MODEL_HUB_ENABLE_STRUCTURE_PREDICTION_MODELS',
      'MODEL_HUB_ENABLE_OPENFOLD3',
    ],
    isPreview: true,
  },
};

/**
 * Get all models that are currently available based on feature flags.
 */
export function getAvailableStructurePredictionModels(): Array<string> {
  return Object.keys(SCIENTIFIC_MODEL_CONFIG)
    .filter((model) => getModelConfig(model).category === MODEL_CATEGORY.STRUCTURE_PREDICTION)
    .filter((model) => {
      const config = getModelConfig(model);
      if (config.requiresFeatureFlags.length > 0) {
        return config.requiresFeatureFlags.every(
          (featureFlag: keyof typeof BenchlingConfig) =>
            (BenchlingConfig as Record<string, unknown>)[featureFlag] === true
        );
      }
      return true;
    });
}

/**
 * Get the default model based on feature flags.
 * When MODEL_HUB_ENABLE_SCIENTIFIC_MODELS is enabled, returns BOLTZ2.
 * Otherwise, returns ALPHAFOLD.
 */
export function getDefaultStructurePredictionModel(): string {
  return BenchlingConfig.MODEL_HUB_ENABLE_STRUCTURE_PREDICTION_MODELS
    ? SCIENTIFIC_MODEL.BOLTZ2
    : SCIENTIFIC_MODEL.ALPHAFOLD;
}

/**
 * Returns the first candidate model that is currently available, falling back to the
 * flag-based default. Use this to resolve the initial model selection when multiple
 * sources of truth exist (e.g. a prior run's model, a stored user preference, and a default).
 */
export function resolveInitialStructurePredictionModel(
  availableModels: Array<string>,
  ...candidates: Array<string | undefined>
): string {
  for (const candidate of candidates) {
    if (candidate != null && availableModels.includes(candidate)) {
      return candidate;
    }
  }
  return getDefaultStructurePredictionModel();
}

export enum ScientificModelPreferences {
  HAS_DISMISSED_MODEL_CONFIDENCE_POPOVER = 'HAS_DISMISSED_MODEL_CONFIDENCE_POPOVER',
  LAST_SELECTED_STRUCTURE_PREDICTION_MODEL = 'LAST_SELECTED_STRUCTURE_PREDICTION_MODEL',
}

/**
 * Get the complete configuration for a structure prediction model.
 * @throws Error if the model is not found in the configuration.
 */
export function getModelConfig(model: string): ScientificModelConfig {
  const config = SCIENTIFIC_MODEL_CONFIG[model];
  if (!config) {
    throw new Error(`Unknown scientific model: ${model}`);
  }
  return config;
}

/**
 * Get the user-friendly display name for a structure prediction model.
 */
export function getModelDisplayName(model: string): string {
  const config = getModelConfig(model);
  return config.displayName;
}

export function getModelIconUrl(model: string): string {
  const config = getModelConfig(model);
  return config.iconUrl;
}

/**
 * Get the external link/documentation URL for a model.
 */
export function getModelExternalLink(model: string): string {
  const config = getModelConfig(model);
  return config.externalLink;
}

/**
 * Get the paper link for a model (falls back to externalLink if paperLink is not provided).
 */
export function getModelPaperLink(model: string): string {
  const config = getModelConfig(model);
  return config.paperLink ?? config.externalLink;
}

/**
 * Check if a model supports multimer predictions.
 */
export function supportsMultimers(model: string): boolean {
  const config = getModelConfig(model);
  return config.supportsMultimers;
}

/**
 * Check if a model supports multimer molecules.
 */
export function supportsMulitmerMolecules(model: string): boolean {
  const config = getModelConfig(model);
  return config.supportsMulitmerMolecules;
}

/**
 * Check if a model is in preview mode.
 */
export function isPreviewModel(model: string): boolean {
  const config = getModelConfig(model);
  return config.isPreview ?? false;
}

/**
 * Check if a model egresses data to third parties.
 */
export function modelEgressesData(model: string): boolean {
  const config = getModelConfig(model);
  return config.egressesData ?? false;
}

/**
 * Get the minimum amino acid count required for structure predictions.
 * Returns null if the model doesn't have a minimum requirement.
 */
export function getMinAminoAcidCount(model: string): number {
  const config = getModelConfig(model);
  return config.minAminoAcidCount ?? MIN_AMINO_ACID_COUNT;
}

/**
 * Get the maximum amino acid count allowed for structure predictions.
 * Currently all models share the same limit.
 */
export function getMaxAminoAcidCount(model: string): number {
  const config = getModelConfig(model);
  return config.maxAminoAcidCount ?? MAX_AMINO_ACID_COUNT;
}

/**
 * Get the maximum number of components allowed in a multimer prediction.
 * Currently all models share the same limit.
 */
export function getMaxMultimerComponents(model: string): number {
  const config = getModelConfig(model);
  if (!config.supportsMultimers) {
    return 1;
  }
  return config.maxMultimerComponents ?? MAX_MULTIMER_COMPONENTS;
}

/**
 * Get the maximum token count allowed for a model.
 * Returns null if the model doesn't have a token limit.
 */
export function getMaxTokenCount(model: string): number | null {
  const config = getModelConfig(model);
  return config.maxTokenCount ?? null;
}
